// What the browser and the game server say to each other during online play.
//
// The server keeps the real game: it runs the same engine as the solo table, checks every move with it,
// and sends each player their own view of the table (so nobody's browser ever holds the other player's
// hand or deck). Messages are JSON, one per WebSocket message.
import type { Action, GameView, Seat } from './engine.ts';
import type { MatchFile } from './match.ts';

/** Raised when the messages change shape, so an old page is told to refresh instead of misbehaving. */
export const PROTOCOL = 6;

/** What the server says when someone tries to take a seat in a game without a deck (the page opens the deck builder). */
export const NEED_DECK = 'You need a deck to play: make one first.';

/** The letters a game code is made of: no 0/O, 1/I/L, so a code read out loud can't be mistyped. */
export const CODE_LETTERS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 5;
export function cleanCode(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
}

/** A game waiting for a second player, as the open games list shows it. */
export interface OpenGame {
  code: string;
  host: string;
  emperor: string; // the host's Emperor
  since: number; // when it was made (ms since 1970)
  teams?: boolean; // a 2v2 game
  seated?: number; // 2v2: how many of its four seats are taken
}

/** A game being played right now, as the list of games to watch shows it. */
export interface LiveGame {
  code: string;
  names: string[];
  emperors: string[];
  since: number; // when it started (ms since 1970)
  watchers: number; // how many people are watching it
}

/** Card statistics from online games (GET /stats): for each card, how many decks played it and how they did. */
export interface CardStats {
  decks: number; // decks played in games that reached 2 global turns
  games: number; // those games
  playDecks?: number; // decks whose games are over (or cleared away), so what was played from them is counted
  simGames?: number; // of `games`, how many were simulated bot games (npm run sim-stats), added as a starting record
  // by card number:
  //   decks / decided / won: decks with it; of those, the ones in games with a winner; and the ones that won
  //   played / playedDecided / playedWon: decks it was actually played from (placed on the table); of those, in
  //     games with a winner; and the ones that won
  cards: Record<string, { decks: number; decided: number; won: number; played?: number; playedDecided?: number; playedWon?: number }>;
}

/** Every message that opens a game carries these: an old page or a different card list is turned away. */
interface Hello {
  protocol: number;
  fingerprint: string;
  account?: string; // the sign-in key of the account this player is logged in as (none for a guest): the server checks the name belongs to them
}

export type ClientMessage =
  | { t: 'list' } // send me the open games, and keep me posted
  | ({ t: 'create'; name: string; deck: number[]; open: boolean; teams?: boolean } & Hello) // teams = a 2v2 game
  | { t: 'seat'; seat: Seat } // 2v2, before the game starts: move to this empty seat
  | { t: 'start' } // 2v2: the host starts the game once all four seats are taken
  | { t: 'fillBot'; seat: Seat; level: string } // the host puts a bot in an empty seat before the game starts (1v1: it starts at once)
  | { t: 'clearBot'; seat: Seat } // 2v2, before the start: the host takes a bot out of its seat
  | ({ t: 'bot'; name: string; deck: number[]; level: string; tutorial?: boolean } & Hello) // tutorial: Martin only, with hints (no card stats) // a game against the computer (level: easy, skilled or expert)
  | ({ t: 'join'; code: string; name: string; deck: number[] } & Hello)
  | ({ t: 'rejoin'; code: string; token: string } & Hello)
  | ({ t: 'watch'; code: string; side: Seat } & Hello) // watch a game being played (side = whose side of the table to look from)
  | { t: 'side'; side: Seat } // a spectator looks from the other side
  | { t: 'cancel' } // the host gives up on a game nobody has joined yet
  | { t: 'action'; action: Action }
  | { t: 'file' } // send me the match so far (to save it, or for the replay)
  | { t: 'chat'; text: string } // say something to everyone at this table (or in this 2v2 lobby)
  | { t: 'leave' }; // I'm leaving this game for good (it stays open for the other player to finish up)

export type ServerMessage =
  | { t: 'lobbies'; games: OpenGame[]; live: LiveGame[] }
  | { t: 'watching'; code: string; names: string[]; side: Seat } // you are watching this game
  // your seat in a game. 2v2: names has four entries ('' = an empty seat) and comes again whenever someone sits or moves
  | { t: 'room'; code: string; seat: Seat; token: string; started: boolean; names: string[]; teams?: boolean; host?: Seat; bots?: number[] } // bots: the seats bots play
  // clock: with the Battle Timer on, whose turn is being timed and how many milliseconds they have left
  | { t: 'view'; view: GameView; elapsed: number; clock?: { seat: Seat; left: number; paused?: boolean } | null } // paused: that player is away (their reconnect time runs instead)
  // who is at the table right now, and how many are watching. away: for each player who has left a game in
  // progress, the milliseconds they have left to come back before they lose by forfeit (null = here, or not counting)
  | { t: 'presence'; connected: boolean[]; watchers: number; away?: (number | null)[] }
  | { t: 'file'; file: MatchFile }
  | { t: 'refused'; message: string } // that move wasn't allowed (the table puts the piece back)
  | { t: 'error'; message: string; fatal?: boolean } // fatal: this game can't go on from here
  | { t: 'closed'; reason: string } // the game is gone (cancelled, or cleared away)
  | { t: 'chat'; line: ChatLine } // something said at this table
  | { t: 'chatLog'; lines: ChatLine[] }; // what has been said so far (sent as you sit down or start watching)

/** One chat message: who said it (their seat; null = a watcher or the table itself), their name, what, and when. */
export interface ChatLine {
  seat: Seat | null;
  name: string;
  text: string;
  at: number;
}
/** How long one chat message may be, and how many the table keeps. */
export const CHAT_MAX = 200;
export const CHAT_KEEP = 80;
